import React, { useState, useEffect } from 'react';
import { Table, Button, Tag, Switch, Form, Input, Select, Space, Tooltip, Badge, Popconfirm, Tabs } from 'antd';
import {
    PlusOutlined, EditOutlined, PoweroffOutlined, SettingOutlined,
    ExclamationCircleOutlined, SyncOutlined,
} from '@ant-design/icons';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { TYPES_DOC_GED, TYPES_DOC_GED_MAP } from '../../variables/constants';
import { toast } from 'react-toastify';
import { Trans, useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { avecLibelleTraduit } from '../../i18n/libelles';
import CBModal from '../../components/CBModal';
import PageHeader from '../../components/PageHeader';

/* Les trois couloirs du bureau : une victime = une liste, sans mélange.
   (Le découpage commun/corporel/mixte est abandonné - fiches papier BNCB.) */
const CATEGORIE_OPTIONS = avecLibelleTraduit([
    { value: 'BLESSURE' },
    { value: 'DECES' },
    { value: 'MATERIEL' },
], 'parametres:pieces.categories');

const TD_COLOR = {
    null: 'default',
    CORPOREL: 'red',
    MATERIEL: 'orange',
    MIXTE: 'volcano',
    DECES: 'purple',
};

const TD_LABEL = {
    null: 'Commun',
    CORPOREL: 'Corporel',
    MATERIEL: 'Matériel',
    MIXTE: 'Mixte',
    DECES: 'Décès',
};

export default function ParametragePieces() {
    const { t } = useTranslation('parametres');
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [resyncEnCours, setResyncEnCours] = useState(false);
    const [activeTab, setActiveTab] = useState('TOUT');
    const [form] = Form.useForm();

    // Correction globale corporel/matériel : retire des checklists les pièces d'un
    // autre type de dommage (sans document) et ajoute les applicables manquantes.
    const resynchroniser = async () => {
        setResyncEnCours(true);
        try {
            const res = await axiosInstance.post('/v1/dossiers/pieces/resynchroniser');
            toast.success(res.data?.data || res.data?.message || t('pieces.messages.checklistsResynchronisees'));
        } catch (e) {
            toast.error(e?.response?.data?.message || t('pieces.messages.echecResynchronisation'));
        } finally {
            setResyncEnCours(false);
        }
    };

    const load = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/v1/parametrage/pieces');
            setData(res.data?.data || []);
        } catch { toast.error(i18n.t('parametres:pieces.messages.erreurChargement')); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setEditing(null);
        form.resetFields();
        // Categorie proposee : celle de l'onglet ouvert. Ces valeurs par defaut ne se
        // posent qu'ici : ecrites dans le corps du composant, elles revenaient a chaque
        // rendu et ecrasaient, en modification, les valeurs du type de piece.
        form.setFieldsValue({
            obligatoire: true, actif: true, ordre: 0,
            categorie: activeTab === 'TOUT' ? 'BLESSURE' : activeTab,
        });
        setModalOpen(true);
    };

    const openEdit = (record) => {
        setEditing(record);
        form.setFieldsValue({
            libelle: record.libelle,
            categorie: record.categorie ?? 'BLESSURE',
            obligatoire: record.obligatoire,
            ordre: record.ordre,
            actif: record.actif,
            typeDocumentGed: record.typeDocumentGed ?? null,
        });
        setModalOpen(true);
    };

    const onSubmit = async (vals) => {
        try {
            const payload = {
                ...vals,
                categorie: vals.categorie,
                typeDocumentGed: vals.typeDocumentGed ?? null,
            };
            if (editing) {
                await axiosInstance.put(`/v1/parametrage/pieces/${editing.trackingId}`, payload);
                toast.success(t('pieces.messages.typeModifie'));
            } else {
                await axiosInstance.post('/v1/parametrage/pieces', payload);
                toast.success(t('pieces.messages.typeCree'));
            }
            setModalOpen(false);
            load();
        } catch { toast.error(t('pieces.messages.erreurSauvegarde')); }
    };

    const doToggle = async (record) => {
        try {
            await axiosInstance.patch(`/v1/parametrage/pieces/${record.trackingId}/toggle`);
            toast.success(record.actif ? t('pieces.messages.desactive') : t('pieces.messages.active'));
            load();
        } catch { toast.error(t('pieces.messages.erreur')); }
    };

    const toggle = (record) => {
        // Confirmation requise avant désactivation uniquement ; la réactivation est directe.
        if (!record.actif) { doToggle(record); return; }
        CBModal.confirm({
            title: t('pieces.desactivation.titre'),
            icon: <ExclamationCircleOutlined style={{ color: '#d97706' }} />,
            content: (
                <span>
                    <Trans t={t} i18nKey="pieces.desactivation.contenu" values={{ libelle: record.libelle }} components={{ 1: <strong /> }} />
                </span>
            ),
            okText: t('pieces.desactivation.desactiver'),
            okButtonProps: { danger: true },
            cancelText: t('pieces.desactivation.annuler'),
            onOk: () => doToggle(record),
        });
    };

    // // Un tableau par couloir, comme les trois fiches papier.
    // const grouped = CATEGORIE_OPTIONS.map(opt => ({
    //     ...opt,
    //     pieces: data.filter(d => d.categorie === opt.value),
    // })).filter(g => g.pieces.length > 0);
    // const sansCouloir = data.filter(d => !d.categorie);
    // if (sansCouloir.length) grouped.push({ value: null, label: t('pieces.groupe.sansCouloir'), pieces: sansCouloir });

    const buildGroups = (tab) => {
        if (tab !== 'TOUT') {
            const opt = CATEGORIE_OPTIONS.find(o => o.value === tab);
            return [{ ...opt, pieces: data.filter(d => d.categorie === tab) }];
        }
        const groups = CATEGORIE_OPTIONS
            .map(opt => ({ ...opt, pieces: data.filter(d => d.categorie === opt.value) }))
            .filter(g => g.pieces.length > 0);
        const sansCouloir = data.filter(d => !d.categorie);
        if (sansCouloir.length)
            groups.push({ value: null, label: t('pieces.groupe.sansCouloir'), pieces: sansCouloir });
        return groups;
    };

    const renderGroups = (tab) => {
        const groups = buildGroups(tab);
        if (!groups.some(g => g.pieces.length)) {
            return <div className="cb-card" style={{ padding: 24, color: '#6b7280' }}>{t('pieces.groupe.aucunePiece')}</div>;
        }
        return groups.map((group, idx) => (
            <div key={String(group.value)} className="cb-card" style={{ marginBottom: 20, padding: 0 }}>
                <div style={{ padding: '14px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <SettingOutlined style={{ color: '#1a6b3c' }} />
                    <Tag color={TD_COLOR[String(group.value)]} style={{ borderRadius: 20, fontSize: 12, padding: '2px 12px' }}>
                        {group.label}
                    </Tag>
                    <span style={{ color: '#6b7280', fontSize: 12 }}>
                        {t('pieces.groupe.nbPieces', { count: group.pieces.length })}
                        {' '}{t('pieces.groupe.nbObligatoires', { count: group.pieces.filter(p => p.obligatoire).length })}
                    </span>
                </div>
                <Table
                    className="cb-table"
                    rowKey="trackingId"
                    dataSource={group.pieces}
                    columns={cols}
                    pagination={false}
                    size="small"
                    showHeader={idx === 0}
                />
            </div>
        ));
    };

    const countOf = (v) => v === 'TOUT' ? data.length : data.filter(d => d.categorie === v).length;

    const tabItems = [
        { key: 'TOUT', label: t('pieces.onglets.tout', { n: countOf('TOUT') }) },
        ...CATEGORIE_OPTIONS.map(o => ({ key: o.value, label: `${o.label} (${countOf(o.value)})` })),
    ];

    const cols = [
        {
            title: t('pieces.colonnes.libelle'), dataIndex: 'libelle', key: 'lib',
            render: (v, r) => (
                <span style={{ fontWeight: r.obligatoire ? 700 : 400, color: r.actif ? '#111827' : '#9ca3af' }}>
                    {v} {r.obligatoire && <Tag color="red" style={{ borderRadius: 20, fontSize: 10, marginLeft: 4 }}>{t('pieces.colonnes.obligatoire')}</Tag>}
                </span>
            ),
        },
        { title: t('pieces.colonnes.ordre'), dataIndex: 'ordre', key: 'ord', width: 70, align: 'center' },
        {
            title: t('pieces.colonnes.typeGed'),
            dataIndex: 'typeDocumentGed',
            key: 'ged',
            width: 220,
            render: (v) => v
                ? <Tag color="blue" style={{ borderRadius: 20, fontSize: 11 }}>{TYPES_DOC_GED_MAP[v] || v}</Tag>
                : <span style={{ color: '#9ca3af', fontSize: 12 }}>{t('pieces.colonnes.manuel')}</span>,
        },
        {
            title: t('pieces.colonnes.statut'), dataIndex: 'actif', key: 'actif', width: 90, align: 'center',
            render: (v) => <Badge status={v ? 'success' : 'default'} text={v ? t('pieces.colonnes.actif') : t('pieces.colonnes.inactif')} />,
        },
        {
            title: t('pieces.colonnes.actions'), key: 'actions', width: 110, align: 'center',
            render: (_, r) => (
                <Space>
                    <Tooltip title={t('pieces.actions.modifier')}>
                        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}
                            style={{ borderRadius: 6 }} />
                    </Tooltip>
                    <Tooltip title={r.actif ? t('pieces.actions.desactiver') : t('pieces.actions.activer')}>
                        <Button size="small" icon={<PoweroffOutlined />}
                            style={{
                                borderRadius: 6, borderColor: r.actif ? '#991b1b' : '#166534',
                                color: r.actif ? '#991b1b' : '#166534'
                            }}
                            onClick={() => toggle(r)} />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <Layout title={t('pieces.page.titre')}>
            <div>

                {/* Aucun filtre : `data` porte toutes les pieces, que l'ecran
                    se contente de regrouper par type de dommage. */}
                <PageHeader
                    titre={t('pieces.page.titre')}
                    icone={<SettingOutlined />}
                    compte={data.length}
                    unite={t('pieces.page.unite')}
                    actions={(
                        <>
                            <Popconfirm
                                title={t('pieces.resynchronisation.titre')}
                                description={t('pieces.resynchronisation.description')}
                                okText={t('pieces.resynchronisation.confirmer')}
                                cancelText={t('pieces.resynchronisation.annuler')}
                                onConfirm={resynchroniser}
                            >
                                <Button icon={<SyncOutlined spin={resyncEnCours} />} loading={resyncEnCours}
                                    style={{ borderRadius: 8 }}>
                                    {t('pieces.actions.resynchroniser')}
                                </Button>
                            </Popconfirm>
                            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}
                                style={{ background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8 }}>
                                {t('pieces.actions.nouveauType')}
                            </Button>
                        </>
                    )}
                />

                {/* Affichage groupé par TypeDommage */}
                {/* {grouped.map(group => (
                    <div key={String(group.value)} className="cb-card" style={{ marginBottom: 20, padding: 0 }}>
                        <div style={{
                            padding: '14px 20px', borderBottom: '1px solid #f3f4f6',
                            display: 'flex', alignItems: 'center', gap: 12
                        }}>
                            <SettingOutlined style={{ color: '#1a6b3c' }} />
                            <Tag color={TD_COLOR[String(group.value)]} style={{ borderRadius: 20, fontSize: 12, padding: '2px 12px' }}>
                                {group.label}
                            </Tag>
                            <span style={{ color: '#6b7280', fontSize: 12 }}>
                                {t('pieces.groupe.nbPieces', { count: group.pieces.length })}
                                {' '}{t('pieces.groupe.nbObligatoires', { count: group.pieces.filter(p => p.obligatoire).length })}
                            </span>
                        </div>
                        <Table
                            className="cb-table"
                            dataSource={group.pieces.map((p, i) => ({ ...p, key: i }))}
                            columns={cols}
                            pagination={false}
                            size="small"
                            showHeader={group === grouped[0]}
                        />
                    </div>
                ))} */}
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={tabItems}
                />
                {renderGroups(activeTab)}

                {/* Modal création / édition */}
                <CBModal
                    title={editing
                        ? <><EditOutlined style={{ marginRight: 8 }} />{t('pieces.modale.titreModification')}</>
                        : <><PlusOutlined style={{ marginRight: 8 }} />{t('pieces.modale.titreCreation')}</>}
                    open={modalOpen}
                    onCancel={() => setModalOpen(false)}
                    footer={null}
                    width={520}
                >
                    <Form form={form} layout="vertical" onFinish={onSubmit} style={{ marginTop: 16 }}>

                        <Form.Item name="libelle" label={t('pieces.modale.libelle')}
                            rules={[{ required: true, message: t('pieces.modale.libelleObligatoire') }]}>
                            <Input placeholder={t('pieces.modale.libelleExemple')} style={{ borderRadius: 8 }} />
                        </Form.Item>

                        <Form.Item name="categorie" label={t('pieces.modale.couloir')}
                            rules={[{ required: true, message: t('pieces.modale.couloirObligatoire') }]}>
                            <Select style={{ borderRadius: 8 }} options={CATEGORIE_OPTIONS}
                                placeholder={t('pieces.modale.couloirExemple')}
                                getPopupContainer={t => t.parentElement} />
                        </Form.Item>

                        <Form.Item name="obligatoire" label={t('pieces.modale.obligatoire')}
                            valuePropName="checked">
                            <Switch checkedChildren={t('pieces.modale.obligatoireOui')} unCheckedChildren={t('pieces.modale.obligatoireNon')} />
                        </Form.Item>

                        <Form.Item name="ordre" label={t('pieces.modale.ordre')}>
                            <Input type="number" style={{ borderRadius: 8, width: 120 }} />
                        </Form.Item>

                        <Form.Item
                            name="typeDocumentGed"
                            label={t('pieces.modale.typeGed')}
                            extra={t('pieces.modale.typeGedAide')}
                        >
                            <Select
                                allowClear
                                placeholder={t('pieces.modale.typeGedAucun')}
                                showSearch
                                filterOption={(input, opt) => opt.label?.toLowerCase().includes(input.toLowerCase())}
                                options={[
                                    { value: null, label: t('pieces.modale.typeGedManuel') },
                                    ...TYPES_DOC_GED.map(t => ({ value: t.value, label: t.label })),
                                ]}
                                getPopupContainer={t => t.parentElement}
                            />
                        </Form.Item>

                        <Form.Item name="actif" label={t('pieces.modale.actif')} valuePropName="checked">
                            <Switch checkedChildren={t('pieces.modale.actif')} unCheckedChildren={t('pieces.modale.inactif')} />
                        </Form.Item>

                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <Button onClick={() => setModalOpen(false)}>{t('pieces.modale.annuler')}</Button>
                            <Button type="primary" htmlType="submit"
                                style={{ background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8 }}>
                                {editing ? t('pieces.modale.modifier') : t('pieces.modale.creer')}
                            </Button>
                        </div>
                    </Form>
                </CBModal>
            </div>
        </Layout>
    );
}
